import { Fragment, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./globals.css";
import App from "./app";

// StrictMode double-renders every component in dev to surface side-effect bugs.
// Combined with React 19's `recordLegacyContextWarning` fiber walk (dev-only,
// not stripped until production build), it multiplies render cost on large
// trees to the point of main-thread saturation on lower-powered devices.
//
// The default is StrictMode ON — matches production runtime semantics and
// catches real bugs. Devs who hit the perf ceiling locally can opt out via
// `VITE_DISABLE_STRICT_MODE=1` in their .env.local. CI + production builds
// never read that var, so the checks always run where it counts.
// Only honour the escape hatch in dev builds — production/CI bundles skip
// the env read entirely, matching the documented guarantee above.
const strictModeEnabled =
  !import.meta.env.DEV || import.meta.env.VITE_DISABLE_STRICT_MODE !== "1";
const RootWrapper = strictModeEnabled ? StrictMode : Fragment;

// Dev error overlay — shows JS errors on screen when dev tools aren't available
if (import.meta.env.DEV) {
  window.addEventListener("error", (e) => {
    const el = document.createElement("pre");
    el.style.cssText =
      "position:fixed;top:0;left:0;right:0;z-index:99999;background:red;color:white;padding:16px;font-size:12px;white-space:pre-wrap;max-height:50vh;overflow:auto";
    el.textContent = `ERROR: ${e.message}\n\n${e.filename}:${e.lineno}\n\n${e.error?.stack ?? ""}`;
    document.body.prepend(el);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const el = document.createElement("pre");
    el.style.cssText =
      "position:fixed;top:0;left:0;right:0;z-index:99999;background:darkred;color:white;padding:16px;font-size:12px;white-space:pre-wrap;max-height:50vh;overflow:auto";
    el.textContent = `UNHANDLED REJECTION: ${e.reason?.message ?? e.reason}\n\n${e.reason?.stack ?? ""}`;
    document.body.prepend(el);
  });
}

const root = document.getElementById("root");
if (!root) {
  throw new Error("Root element not found");
}

createRoot(root).render(
  <RootWrapper>
    <App />
  </RootWrapper>
);
